<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink } from 'vue-router'
import { Smartphone, Car, Building2, Wheat, Gem, Palette, Tractor, Layers, Sofa } from 'lucide-vue-next'
import { platformCategories } from '@/data/categories'
import { useI18n } from '@/composables/useI18n'
import CategoryArt from '@/components/category/CategoryArt.vue'
import { useThemeStore } from '@/stores/theme'

interface Props {
  counts?: Record<string, number>
}

const props = withDefaults(defineProps<Props>(), { counts: () => ({}) })
const { t, currentLocale } = useI18n()
// Admin > Design > Categories: 'shyrdak' | 'object' (our vector art) or 'photo'.
const artStyle = computed(() => useThemeStore().theme.categoryArtStyle || 'shyrdak')

const iconMap: Record<string, any> = {
  electronics: Smartphone,
  vehicles: Car,
  'real-estate': Building2,
  livestock: Wheat,
  jewelry: Gem,
  art: Palette,
  machinery: Tractor,
  'home-goods': Sofa,
}

const tiles = computed(() => {
  const lang = (currentLocale.value?.code as 'ky' | 'ru' | 'tr') || 'ru'
  return platformCategories.map(cat => ({
    slug: cat.slug,
    name: cat.name[lang] || cat.name.ru,
    // The phone grid has room for a word, not for "Emlak & Ticari Dükkanlar".
    shortName: cat.shortName?.[lang] || cat.shortName?.ru || cat.name[lang] || cat.name.ru,
    image: cat.coverImage,
    icon: iconMap[cat.slug] || Layers,
    count: props.counts[cat.slug] || 0,
  }))
})
</script>

<template>
  <section aria-labelledby="categories-title" class="space-y-4">
    <div class="flex items-start justify-between gap-3 sm:items-end">
      <div class="min-w-0">
        <h2 id="categories-title" class="font-display text-xl sm:text-2xl font-bold tracking-tight text-text-primary">
          {{ t('landing.categoriesTitle') }}
        </h2>
        <p class="mt-0.5 hidden text-sm text-text-secondary sm:block">{{ t('landing.categoriesSubtitle') }}</p>
      </div>
      <RouterLink
        to="/categories"
        class="-mt-1.5 -mx-2 shrink-0 whitespace-nowrap rounded-lg px-2 py-2.5 text-sm font-semibold text-text-primary hover:text-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:mt-0 sm:-mb-2.5"
      >
        {{ t('common.viewAll') }} →
      </RouterLink>
    </div>

    <!-- Phones: the whole taxonomy in two rows of four, so the first screen
         answers "what is sold here?" without scrolling. The art cards below
         need width to work and take over from lg up. -->
    <div class="grid grid-cols-4 gap-1.5 sm:gap-2 lg:hidden">
      <RouterLink
        v-for="tile in tiles"
        :key="`c-${tile.slug}`"
        :to="`/auctions?category=${tile.slug}`"
        class="flex flex-col items-center gap-1.5 rounded-xl border border-border bg-surface px-0.5 py-2.5 text-center transition active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span class="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <component :is="tile.icon" class="h-[18px] w-[18px]" />
        </span>
        <span class="line-clamp-2 w-full break-words text-[10px] font-semibold max-[359px]:text-[9px] leading-tight tracking-tight text-text-primary sm:text-[11px]">{{ tile.shortName }}</span>
        <span v-if="tile.count > 0" class="text-[10px] tabular text-text-muted">{{ tile.count }}</span>
      </RouterLink>
    </div>

    <div class="hidden lg:mx-0 lg:grid lg:grid-cols-4 lg:gap-3 lg:overflow-visible lg:px-0 xl:grid-cols-8">
      <RouterLink
        v-for="tile in tiles"
        :key="tile.slug"
        :to="`/auctions?category=${tile.slug}`"
        class="group relative block w-[148px] sm:w-[168px] lg:w-auto aspect-[4/5] lg:aspect-[16/10] xl:aspect-[4/5] overflow-hidden rounded-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        :class="artStyle === 'photo' ? 'bg-ink' : 'border border-border'"
      >
        <template v-if="artStyle === 'photo'">
          <img
            :src="tile.image"
            :alt="tile.name"
            loading="lazy"
            class="absolute inset-0 h-full w-full object-cover opacity-90 transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div class="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/20 to-transparent" />
        </template>
        <div v-else class="absolute inset-0 transition-transform duration-500 ease-out group-hover:scale-[1.04]">
          <CategoryArt :slug="tile.slug" :variant="artStyle" class="lg:hidden xl:block" />
          <CategoryArt :slug="tile.slug" :variant="artStyle" layout="wide" class="hidden lg:block xl:hidden" />
        </div>
        <div class="absolute inset-x-0 p-3" :class="artStyle === 'photo' ? 'bottom-0' : 'bottom-[11%] pb-1.5'">
          <span v-if="artStyle === 'photo'" class="mb-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-onPrimary shadow">
            <component :is="tile.icon" class="h-4 w-4" />
          </span>
          <p class="text-[13px] font-semibold leading-tight line-clamp-2" :class="artStyle === 'photo' ? 'text-white' : 'text-text-primary'">{{ tile.name }}</p>
          <p v-if="tile.count > 0" class="mt-0.5 text-[11px] tabular" :class="artStyle === 'photo' ? 'text-white/65' : 'text-text-secondary'">{{ tile.count }} {{ t('landing.lots') }}</p>
        </div>
      </RouterLink>
    </div>
  </section>
</template>
